import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';

import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { FormInputComponent } from '~/component/form/input/input.component';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';

interface EquipmentType {
  attributeName: string;
  keyName: string;
}

@Component({
  selector: 'app-organizer-activity-edition-equipement',
  templateUrl: './equipment.component.html',
})
export class OrganizerActivityEditionEquipmentComponent
  implements OnInit, OnDestroy
{
  @ViewChildren('equipmentInputs')
  public inputComponents: QueryList<FormInputComponent>;
  private readonly subscriptions = new Subscription();

  public readonly addButtonIconType = VtmnPlayIconTypeEnum.Add;
  public readonly addButtonVariant = CtaVariantEnum.Link2;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly crossIconType = VtmnPlayIconTypeEnum.Close;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;

  public activity: Activity;
  public equipmentsForm: FormGroup;
  public equipmentType: EquipmentType;
  public isFormValid: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    private readonly translateService: TranslateService,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly locatableService: OrganizerSharedLocatableService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.equipmentType = this.equipmentTypeParam;

    // build equipments form
    this.equipmentsForm = new FormGroup({
      equipments: new FormArray([]),
    });

    this.subscriptions.add(
      this.equipmentsForm.valueChanges.subscribe(() => {
        this.isFormValid =
          this.equipmentsForm.valid && this.equipmentsForm.dirty;
      })
    );

    this.subscriptions.add(
      this.activatedRoute.data
        .pipe(
          mergeMap(({ activityData }) => {
            return this.organizerSharedActivityService.getActivity(
              activityData.activity?.sku
            );
          })
        )
        .subscribe((activity) => {
          activity[this.equipmentType.attributeName]?.forEach((equipment) =>
            this.equipments.push(
              new FormControl(equipment, noHtmlTagValidator())
            )
          );
          this.activity = activity;
        })
    );
  }

  public get equipments(): FormArray {
    return this.equipmentsForm.controls.equipments as FormArray;
  }

  public get equipmentTypeParam(): EquipmentType {
    const route = NavigationService.getCurrentName(this.activatedRoute);
    let equipmentType = { attributeName: '', keyName: '' };

    if (route.includes('equipment-provided')) {
      equipmentType = {
        attributeName: 'providedEquipment',
        keyName: 'equipment-provided',
      };
    }
    if (route.includes('equipment-required')) {
      equipmentType = {
        attributeName: 'requiredEquipment',
        keyName: 'equipment-required',
      };
    }

    return equipmentType;
  }

  public get hasInitialValue(): boolean {
    if (this.equipmentType.keyName === 'equipment-required') {
      return Boolean(this.activity?.requiredEquipment?.length);
    }

    return Boolean(this.activity?.providedEquipment?.length);
  }

  private cleanEquipments(): string[] {
    return this.equipments.controls
      .map((control) => control.value)
      .filter((name) => name); // Clear empty string.
  }

  public addEquipment($event?: Event): void {
    $event?.preventDefault();

    this.equipments.push(
      new FormControl('', [Validators.required, noHtmlTagValidator()])
    );
    this.focusEquipmentInput(this.equipments?.length - 1);
  }

  public deleteEquipment(equipmentIndex: number): void {
    this.equipments.removeAt(equipmentIndex);
  }

  public focusEquipmentInput(index: number): void {
    /**
     * setTimeout is used to prevent changes after checked
     */
    setTimeout(() => {
      this.inputComponents.forEach((input, inputIndex) => {
        if (inputIndex === index) {
          input.focus();
        }
      });
    });
  }

  public translateKey(key: string, interpolateParams?: object): string {
    const baseKey = `organizer.edit.${this.equipmentType.keyName}`;

    return this.translateService.instant(`${baseKey}${key}`, interpolateParams);
  }

  public updateEquipment(): void {
    const attributeName = this.equipmentType.attributeName;
    const equipments = this.cleanEquipments();
    this.isOverlayDisplayed = true;

    this.organizerSharedActivityService
      .patchActivityById(this.activity.sku, {
        [attributeName]: [
          this.locatableService.buildCurrentLocatableBody<string[]>(equipments),
        ],
      })
      .pipe(
        mergeMap(() => {
          return this.organizerSharedActivityService
            .getActivity(this.activity?.sku.toString())
            .pipe(
              repeatUntil(
                (activity) =>
                  JSON.stringify(activity[attributeName]) ===
                  JSON.stringify(equipments),
                3000
              )
            );
        })
      )
      .subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      });
  }
}
